import type { ReactElement } from "react";
import type { LamaParticipationTrendOutput } from "@playbookai/widget-schemas";
import type { WidgetBodyProps } from "../types.js";
import {
  LAMA_CONFIDENCE_TONE,
  LAMA_LIVE_WAVE_NOTE,
  lamaConfidenceLabel,
  lamaParticipationDetail,
} from "../lama-report-presentation.js";
import { statusToken } from "../../lib/types.js";
import { cn } from "../../lib/utils.js";
import { LamaParticipationChart } from "./ParticipationChart.js";

/**
 * LAMA Participation by Wave — coverage per wave. Data-blind: the input is the
 * `lama/participation-trend@1` dataset output's `points`.
 *
 * SHOWS THE DENOMINATOR, NOT JUST THE BAR (PB-191 Task 20). A bar height is a
 * percentage, and a percentage without its denominator is not an honest figure —
 * "50%" is the same bar for 1 of 2 and 40 of 80. The latest wave's numerator,
 * denominator and confidence band therefore render as text beside the chart,
 * where a screen reader reaches them too.
 *
 * `submitted`, `total` and `confidence` are OPTIONAL on the wire, so this
 * degrades to the percentage alone rather than rendering "undefined of
 * undefined" — an old resolver serving a new widget is a real rollout state, not
 * a defensive branch.
 *
 * The OPEN wave is still accruing (§13.4), so a `live` point earns the note: a
 * half-height bar means "in progress", not "poor turnout".
 */
export function LamaParticipationTrendWidget({
  data,
}: WidgetBodyProps<LamaParticipationTrendOutput>): ReactElement {
  if (data.points.length === 0) {
    return (
      <p className="text-sm text-pb-text-muted">
        No participation available — coverage per wave will appear when
        snapshots are available.
      </p>
    );
  }
  const latest = data.points[data.points.length - 1]!;
  const hasLivePoint = data.points.some((point) => point.live);
  const detail =
    latest.submitted !== undefined && latest.total !== undefined
      ? lamaParticipationDetail(latest.submitted, latest.total)
      : null;
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-sm font-medium text-pb-text">
          {latest.label}: {latest.participation}%
        </span>
        {detail !== null ? (
          <span className="text-xs text-pb-text-muted">{detail}</span>
        ) : null}
        {latest.confidence !== undefined ? (
          /* Confidence carries a TEXT label as well as a tone class — the band is
             never encoded by colour alone (Task 23). */
          <span
            className={cn(
              "text-xs",
              statusToken(LAMA_CONFIDENCE_TONE[latest.confidence]).textClass,
            )}
          >
            {lamaConfidenceLabel(latest.confidence)} confidence
          </span>
        ) : null}
      </div>
      <LamaParticipationChart
        data={data.points.map(
          ({ label, participation, submitted, total, date }) => ({
            label,
            participation,
            date,
            ...(submitted !== undefined ? { submitted } : {}),
            ...(total !== undefined ? { total } : {}),
          }),
        )}
      />
      {hasLivePoint ? (
        <p className="mt-2 text-xs text-pb-text-muted">{LAMA_LIVE_WAVE_NOTE}</p>
      ) : null}
    </div>
  );
}
